/**
 * ListRow — Push 81 (2026-07-23) row primitive companion to `<ListViewShell>`.
 * ============================================================================
 * Wraps a table row (`<tr>`) with:
 *   - Unified row-click drill (Push 51 interactive-descendant bail-out
 *     discipline) — the row click handler skips descendants matching
 *     `input, select, textarea, button, a, label, .kebab-wrap,
 *     [role="menuitem"], [data-inline-editable], .tag-chip-editor`.
 *   - Optional bulk-select checkbox column (leftmost, rendered only when
 *     `bulkSelectId` + `onBulkSelectChange` are provided).
 *   - Optional row actions menu column (rightmost, rendered only when
 *     `kebabActions` is non-empty), on Foundation's Menu (PLAT-249).
 *
 * Consumers own their `<td>` cells for every column between the optional
 * checkbox + kebab. The primitive doesn't dictate column count or column
 * shape — it only owns the row-click drill + the chevron cells.
 *
 * Table-shape only. Card-list rows (AdminLibraryList document rows) do NOT
 * use this primitive — they'd need a `<div>` shape variant that would either
 * fork logic or force card-list markup into a table-shape it doesn't want.
 * Push 82 sweep may extend to card-list surfaces if the shape lands cleanly.
 *
 * See PATTERNS.md § D "List view shape" for the full pattern context.
 */

import { Button, Checkbox, Menu, MenuContent, MenuItem, MenuTrigger } from '@olytic/foundation';
import { type MouseEvent, type ReactNode, useRef } from 'react';

// ---------------------------------------------------------------------------
// Interactive-descendant bail-out selector — matches Push 51 People.tsx +
// Push 78 SpendLimitsByUserSection + Push 79 AdminLibraryList discipline.
// Any click on a descendant matching this selector skips the row-click drill
// so nested interactive elements keep their own semantics.
// [role="menuitem"]: Foundation's menu items are divs in a portal, and React
// bubbles their clicks up to the row (PLAT-249).
// ---------------------------------------------------------------------------

const INTERACTIVE_DESCENDANT_SELECTOR =
  'input, select, textarea, button, a, label, .kebab-wrap, [role="menuitem"], [data-inline-editable], .tag-chip-editor';

/**
 * Shared bail-out helper — exported for card-list surfaces (AdminLibraryList
 * AdminDocumentRow, etc.) that render `<div>` rows instead of `<tr>` rows.
 * Keeps the interactive-descendant selector in one place.
 *
 * Usage:
 *   const handleClick = (e: MouseEvent<HTMLElement>) => {
 *     if (isInteractiveDescendantClick(e)) return;
 *     navigate('...');
 *   };
 */
export function isInteractiveDescendantClick(
  event: MouseEvent<HTMLElement>,
): boolean {
  const target = event.target as HTMLElement | null;
  if (target === null) return false;
  return target.closest(INTERACTIVE_DESCENDANT_SELECTOR) !== null;
}

// ---------------------------------------------------------------------------
// ListRow props
// ---------------------------------------------------------------------------

export interface ListRowProps {
  /**
   * Row-click drill handler. Fires only when the click target is NOT an
   * interactive descendant. Consumer typically calls `navigate('...')` in
   * the handler. Omit to render a static row without drill semantics.
   */
  onRowClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
  /**
   * Row actions — when non-empty, primitive renders a rightmost `<td>`
   * column with a `<RowActionsMenu>` trigger.
   */
  kebabActions?: RowAction[];
  /**
   * aria-label for the kebab trigger button (matches People.tsx pattern
   * "Row actions for {name}").
   */
  kebabTriggerLabel?: string;
  /**
   * When provided, primitive renders a leftmost `<td>` column with a
   * Foundation `<Checkbox>`. Consumer's `<thead>` should match with a matching
   * leftmost `<th>` (typically carrying the select-all header checkbox).
   */
  bulkSelectId?: string;
  /** Whether this row's checkbox is currently checked. */
  bulkSelected?: boolean;
  /** Fires on checkbox change. */
  onBulkSelectChange?: (next: boolean) => void;
  /** Grays out the checkbox (matches People.tsx isMe / bulkBusy discipline). */
  bulkSelectDisabled?: boolean;
  /** aria-label for the row's bulk checkbox (matches Push 66 discipline). */
  bulkSelectAriaLabel?: string;
  /** Show the bulk-select cell EVEN when this row's own checkbox is hidden
   *  (rare — for a "you can't select yourself" row that must still align
   *  with sibling rows' checkbox column). Default false. */
  bulkSelectHidden?: boolean;
  /** Row-level cursor override — defaults to 'pointer' when `onRowClick`
   *  is provided; consumers can force 'default' for locked-editing states
   *  (matches People.tsx isEditingName). */
  cursorMode?: 'pointer' | 'default';
  /** Consumer's row cells — everything between the checkbox + kebab columns. */
  children: ReactNode;
  /** Optional key override (usually the row's id — consumer sets this on
   *  the `<ListRow>` element itself, not via prop). */
}

export function ListRow({
  onRowClick,
  kebabActions,
  kebabTriggerLabel,
  bulkSelectId,
  bulkSelected = false,
  onBulkSelectChange,
  bulkSelectDisabled = false,
  bulkSelectAriaLabel,
  bulkSelectHidden = false,
  cursorMode,
  children,
}: ListRowProps) {
  const hasBulkColumn =
    bulkSelectId !== undefined && onBulkSelectChange !== undefined;
  const hasKebabColumn = kebabActions !== undefined && kebabActions.length > 0;

  function handleClick(e: MouseEvent<HTMLTableRowElement>) {
    if (onRowClick === undefined) return;
    if (isInteractiveDescendantClick(e)) return;
    onRowClick(e);
  }

  const effectiveCursor =
    cursorMode ?? (onRowClick !== undefined ? 'pointer' : 'default');

  return (
    <tr
      onClick={onRowClick !== undefined ? handleClick : undefined}
      style={{ cursor: effectiveCursor }}
    >
      {hasBulkColumn && (
        <td style={{ width: 32 }}>
          {!bulkSelectHidden && (
            <Checkbox
              {...(bulkSelectAriaLabel !== undefined
                ? { 'aria-label': bulkSelectAriaLabel }
                : {})}
              checked={bulkSelected}
              // Foundation's Checkbox (Radix) reports a boolean on click; a
              // row box is never 'indeterminate' (PLAT-278).
              onCheckedChange={(next) => onBulkSelectChange!(next === true)}
              disabled={bulkSelectDisabled}
            />
          )}
        </td>
      )}
      {children}
      {hasKebabColumn && (
        <td className="col-right" style={{ width: 40 }}>
          <RowActionsMenu
            actions={kebabActions!}
            {...(kebabTriggerLabel !== undefined ? { label: kebabTriggerLabel } : {})}
          />
        </td>
      )}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// RowActionsMenu — a row's ••• menu, on Foundation's Menu (PLAT-249). Also
// used by LibraryList's document and folder rows and People's invitations.
// ---------------------------------------------------------------------------

/** One menu item. Keep its shape: SettingsUsage still builds its row
 *  actions with the local menu's own action type (PLAT-249). */
export interface RowAction {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/**
 * Renders nothing without actions. `label` names the trigger.
 *
 * Not modal, as the local kebab menu wasn't: the page stays scrollable and
 * readable while it's open. That also lets an item put focus back on the
 * trigger before its action runs (the menu-button pattern, PLAT-248), so a dialog the action
 * opens returns focus there when it closes; a modal menu would pull it back.
 */
export function RowActionsMenu({
  actions,
  label = 'Row actions',
}: {
  actions: RowAction[];
  label?: string;
}) {
  const trigger = useRef<HTMLButtonElement | null>(null);
  if (actions.length === 0) return null;
  return (
    <Menu modal={false}>
      <MenuTrigger asChild>
        <Button ref={trigger} variant="subtle" size="icon-sm" aria-label={label}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <circle cx="3" cy="8" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="13" cy="8" r="1.5" />
          </svg>
        </Button>
      </MenuTrigger>
      <MenuContent align="end">
        {actions.map((a) => (
          <MenuItem
            key={a.label}
            destructive={a.danger === true}
            disabled={a.disabled === true}
            onSelect={() => {
              trigger.current?.focus();
              a.onSelect();
            }}
          >
            {a.label}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}
